import { useEffect, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart3,
  Blocks,
  BookOpen,
  Boxes,
  ChevronRight,
  ExternalLink,
  Menu,
  Moon,
  ShieldCheck,
  Sun,
  UserRound,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'

import { api } from '../api'
import { LanguageControl } from './language-control'

const primaryNavigation = [
  { to: '/projects', key: 'Projects', icon: Blocks },
  { to: '/deployments', key: 'Deployments', icon: Boxes },
  { to: '/usage', key: 'Usage', icon: BarChart3 },
  { to: '/docs', key: 'Documentation', icon: BookOpen },
  { to: '/account', key: 'Account', icon: UserRound },
] as const

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const session = useQuery({ queryKey: ['session'], queryFn: api.me })
  const [navigationOpen, setNavigationOpen] = useState(false)
  const [dark, setDark] = useState(
    () => window.localStorage.getItem('computinger-developer-theme') === 'dark'
  )

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark)
    window.localStorage.setItem('computinger-developer-theme', dark ? 'dark' : 'light')
  }, [dark])
  const navigation = session.data?.account.administrator
    ? [
        ...primaryNavigation,
        { to: '/admin/overview', key: 'Administration', icon: ShieldCheck } as const,
      ]
    : primaryNavigation
  const projectMatch = location.pathname.match(/^\/projects\/([^/]+)/u)
  const selectedProjectId = projectMatch?.[1] ? decodeURIComponent(projectMatch[1]) : null
  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.projects(),
    enabled: Boolean(selectedProjectId),
  })
  const currentProject = projects.data?.items.find(
    (project) => project.publicId === selectedProjectId
  )
  const currentSection = location.pathname.startsWith('/deployments')
    ? 'Deployments'
    : location.pathname.startsWith('/usage')
      ? 'Usage'
      : location.pathname.startsWith('/docs')
        ? 'Documentation'
        : location.pathname.startsWith('/admin')
          ? 'Administration'
          : location.pathname.startsWith('/account')
            ? 'Account'
            : 'Projects'

  return (
    <div className='app-shell'>
      <aside className={`sidebar ${navigationOpen ? 'is-open' : ''}`}>
        <div className='brand'>
          <Link className='brand-home' to='/projects' onClick={() => setNavigationOpen(false)}>
            <span className='brand-mark' aria-hidden='true'>
              C
            </span>
            <span>
              <strong>Computinger</strong>
              <small>{t('Developer Platform')}</small>
            </span>
          </Link>
          <button
            className='icon-button mobile-only'
            type='button'
            aria-label={t('Close navigation')}
            onClick={() => setNavigationOpen(false)}
          >
            <X size={18} />
          </button>
        </div>
        <nav aria-label={t('Primary navigation')}>
          <span className='sidebar-section-label'>{t('Developer Center')}</span>
          {navigation.map(({ to, key, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setNavigationOpen(false)}
              className={({ isActive }) =>
                isActive ||
                (to === '/admin/overview' && location.pathname.startsWith('/admin/'))
                  ? 'active'
                  : undefined
              }
            >
              <Icon size={17} strokeWidth={1.8} />
              <span>{t(key)}</span>
            </NavLink>
          ))}
        </nav>
        <div className='sidebar-foot'>
          <a href='https://www.computinger.com' rel='noreferrer'>
            {t('Back to Computinger')}
            <ExternalLink size={13} aria-hidden='true' />
          </a>
        </div>
      </aside>

      <div className='main-frame'>
        <header className='topbar'>
          <button
            className='icon-button mobile-only'
            type='button'
            aria-label={t('Open navigation')}
            onClick={() => setNavigationOpen(true)}
          >
            <Menu size={19} />
          </button>
          <div className='topbar-context'>
            {selectedProjectId ? (
              <>
                <Link to='/projects'>{t('Projects')}</Link>
                <ChevronRight size={14} aria-hidden='true' />
                {currentProject ? (
                  <select
                    aria-label={t('Project switcher')}
                    value={currentProject.publicId}
                    onChange={(event) => navigate(`/projects/${event.target.value}`)}
                  >
                    {projects.data?.items.map((project) => (
                      <option key={project.publicId} value={project.publicId}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span>{selectedProjectId}</span>
                )}
              </>
            ) : (
              <span>{t(currentSection)}</span>
            )}
          </div>
          <div className='topbar-actions'>
            <LanguageControl />
            <button
              className='icon-button'
              type='button'
              aria-label={dark ? t('Use light theme') : t('Use dark theme')}
              onClick={() => setDark((value) => !value)}
            >
              {dark ? <Sun size={17} /> : <Moon size={17} />}
            </button>
            {session.data ? (
              <NavLink className='topbar-user' to='/account' aria-label={t('Account')}>
                <span className='topbar-avatar' aria-hidden='true'>
                  {session.data.account.displayName.slice(0, 1).toUpperCase()}
                </span>
                <span>{session.data.account.displayName}</span>
              </NavLink>
            ) : null}
          </div>
        </header>
        <main className={`content ${selectedProjectId ? 'content-project' : ''}`}>
          {children}
        </main>
      </div>
      {navigationOpen ? (
        <button
          type='button'
          className='scrim'
          aria-label={t('Close navigation')}
          onClick={() => setNavigationOpen(false)}
        />
      ) : null}
    </div>
  )
}
